import { useCallback, useEffect, useState } from "react";
import { App } from "@capacitor/app";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { isSellerOtaTestingEnabled } from "@/lib/native/runtime";
import { keys } from "@/lib/i18n/portal/index";
import { translateStandalone } from "@/lib/i18n/standalone";
import {
  checkSellerOta,
  confirmSellerOtaBoot,
  type SellerOtaCheckResult,
} from "../../lib/native/ota-update";

const OTA_CHECK_INTERVAL_MS = 3 * 60 * 1000;
let lastCheckedAt = 0;

async function checkWhenDue(): Promise<SellerOtaCheckResult | null> {
  if (Date.now() - lastCheckedAt < OTA_CHECK_INTERVAL_MS) return null;
  lastCheckedAt = Date.now();
  const result = await checkSellerOta();
  if (result.state === "error") {
    console.warn("[seller-ota]", result.detail);
  }
  return result;
}

export function NativeOtaBootstrap() {
  const [stagedVersion, setStagedVersion] = useState<string | null>(null);
  const [isReloading, setIsReloading] = useState(false);
  const enabled = isSellerOtaTestingEnabled();
  const embeddedVersion =
    (import.meta.env.VITE_SELLER_OTA_VERSION as string | undefined) ?? "dev";

  const runCheck = useCallback(async () => {
    const result = await checkWhenDue();
    if (result?.state === "staged" && result.version) {
      setStagedVersion(result.version);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let active = true;
    // Boot confirmation is best-effort. A fresh install (or a native plugin
    // that has no pending bundle) may reject it, but that must never prevent
    // the manifest check that surfaces an available update to the seller.
    void confirmSellerOtaBoot()
      .catch((error: unknown) => {
        console.warn(
          "[seller-ota] boot confirmation failed",
          error instanceof Error ? error.message : error,
        );
      })
      .finally(() => {
        if (active) void runCheck();
      });

    const appStateHandle = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) void runCheck();
    });

    return () => {
      active = false;
      void appStateHandle.then((handle) => handle.remove());
    };
  }, [enabled, runCheck]);

  const reloadToApply = useCallback(() => {
    if (!stagedVersion || isReloading) return;
    setIsReloading(true);
    void CapacitorUpdater.reload().catch(() => {
      setIsReloading(false);
    });
  }, [isReloading, stagedVersion]);

  if (!enabled) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-[60] mx-auto flex max-w-md items-center justify-between gap-3 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 shadow-lg"
    >
      <span className="text-sm text-[color:var(--text-primary)]">
        {stagedVersion
          ? translateStandalone(keys.pwa.nativeUpdateReady)
          : translateStandalone(keys.pwa.nativeTestingVersion, {
              version: embeddedVersion,
            })}
      </span>
      {stagedVersion ? (
        <button
          type="button"
          onClick={reloadToApply}
          disabled={isReloading}
          className="shrink-0 rounded-lg bg-[color:var(--primary-500)] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-[color:var(--primary-600)] disabled:cursor-wait disabled:opacity-60"
        >
          {isReloading
            ? translateStandalone(keys.pwa.downloading)
            : translateStandalone(keys.pwa.restartToUpdate)}
        </button>
      ) : null}
    </div>
  );
}
